import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import Intro from "../components/Intro";
import {
  Car, Bike, HeartPulse, Users, Building2, ShieldCheck, ArrowRight,
  Phone, Wallet, HandHeart, MessagesSquare, FileCheck2, UserCheck, BellRing,
} from "lucide-react";
import { MaskedLine, Reveal, Marquee } from "../components/motion";
import QuoteForm from "../components/QuoteForm";
import { PHONE, PHONE_TEL, PHONE_DISPLAY, EMAIL, EMAIL_MAILTO, ADDRESS, MAPS_EMBED, WHATSAPP_URL } from "../lib/site";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { pocketbimaFaqs as faqs } from "../data/faqs";

const finder = [
  { icon: Car, title: "My Car", desc: "Car Insurance", to: "/motor-insurance" },
  { icon: Bike, title: "My Bike", desc: "Bike Insurance", to: "/motor-insurance" },
  { icon: HeartPulse, title: "My Health", desc: "Health Insurance", to: "/health-insurance" },
  { icon: Users, title: "My Family", desc: "Life & Term Insurance", to: "/life-insurance" },
  { icon: Building2, title: "My Business", desc: "Business Insurance", to: "/business-insurance" },
];

const services = [
  {
    title: "Motor Insurance",
    items: ["Car Insurance", "Bike Insurance", "Taxi Insurance", "GCV Insurance", "Third Party Insurance"],
    cta: "Explore Motor Insurance",
    to: "/motor-insurance",
  },
  {
    title: "Health Insurance",
    items: ["Health Insurance", "Group Health Insurance", "Group Personal Accident Insurance"],
    cta: "Explore Health Insurance",
    to: "/health-insurance",
  },
  {
    title: "Life & Protection",
    items: ["Life Insurance", "Term Insurance", "Group Term Insurance"],
    cta: "Protect Your Family",
    to: "/life-insurance",
  },
  {
    title: "Business Insurance",
    items: ["Fire Insurance", "Workmen Compensation Insurance", "Marine Insurance", "Single Transit Insurance"],
    cta: "Protect Your Business",
    to: "/business-insurance",
  },
  {
    title: "Travel Insurance",
    items: ["Travel with confidence with insurance protection designed for your journey."],
    cta: "Explore Travel Insurance",
    to: "/travel-insurance",
  },
];

const why = [
  { icon: Wallet, title: "Simple", desc: "Insurance shouldn't be complicated." },
  { icon: HandHeart, title: "Affordable", desc: "Protection designed for different budgets and needs." },
  { icon: UserCheck, title: "Personalized", desc: "Solutions based on your requirements." },
  { icon: MessagesSquare, title: "Convenient", desc: "Get assistance through phone and digital channels." },
  { icon: FileCheck2, title: "Transparent", desc: "Clear communication and straightforward guidance." },
  { icon: Users, title: "Human Support", desc: "Talk to a real insurance professional when you need help." },
];

const steps = [
  { n: "01", title: "Tell Us Your Need", desc: "Choose the type of insurance you are looking for." },
  { n: "02", title: "Share Your Details", desc: "Provide the required information so suitable options can be identified." },
  { n: "03", title: "Understand Your Options", desc: "Get guidance on available insurance options, coverage and important terms." },
  { n: "04", title: "Get Protected", desc: "Proceed with the policy that suits your requirements." },
];

const chapters = [
  {
    n: "01",
    title: "Insurance shouldn't feel complicated.",
    text: "We believe insurance should not feel complicated, expensive or difficult to understand. PocketBima was created with a simple idea — protection should fit everyone's pocket.",
  },
  {
    n: "02",
    title: "Everything you care about, within reach.",
    text: "Whether it is your car, bike, health, family, journey or business, the right insurance can help you prepare for life's uncertainties.",
  },
  {
    n: "03",
    title: "Customer-first, always.",
    text: "PocketBima brings insurance solutions closer to you — with simple guidance, convenient communication and a customer-first approach.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  const [intro, setIntro] = useState(
    () =>
      !sessionStorage.getItem("pb_intro") &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    if (!intro) return;
    const t = setTimeout(() => {
      setIntro(false);
      sessionStorage.setItem("pb_intro", "1");
    }, 1900);
    return () => clearTimeout(t);
  }, [intro]);

  return (
    <main data-testid="home-page">
      <AnimatePresence>{intro && <Intro />}</AnimatePresence>
      {/* HERO */}
      <section ref={heroRef} className="hero-grid-bg relative overflow-hidden bg-ice" data-testid="hero-section">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[540px] w-[540px] rounded-full bg-mint/15 blur-3xl" />
        <div className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-skycyan/15 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 pt-16 sm:px-8 md:px-12 lg:grid-cols-[1.1fr_1fr] lg:pb-28 lg:pt-24">
          {!intro && (
            <>
          <motion.div style={{ y: textY }}>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-mint/30 bg-mint-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-mint-700"
              data-testid="hero-tagline-chip"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Chhoti Pocket, Badi Suraksha
            </motion.div>
            <h1 className="mt-7 font-display text-5xl font-extrabold leading-[1.02] tracking-tighter text-navy sm:text-6xl lg:text-7xl" data-testid="hero-headline">
              <MaskedLine delay={0.15}>Insurance That</MaskedLine>
              <MaskedLine delay={0.3}>Fits Your</MaskedLine>
              <MaskedLine delay={0.45}>
                <span className="text-mint">Pocket.</span>
              </MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-charcoal md:text-lg"
              data-testid="hero-subheadline"
            >
              Simple, reliable and affordable insurance solutions for you, your family, your
              vehicle and your business.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <a
                href="#quote"
                className="group flex items-center gap-2 rounded-full bg-mint px-8 py-4 text-base font-bold text-white shadow-mint transition-all duration-200 hover:-translate-y-0.5 hover:bg-mint-600"
                data-testid="hero-quote-button"
              >
                Get a Free Quote
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </a>
              <a
                href={PHONE_TEL}
                className="flex items-center gap-2 rounded-full border-2 border-navy/15 px-8 py-[14px] text-base font-bold text-navy transition-colors duration-200 hover:border-navy hover:bg-navy hover:text-white"
                data-testid="hero-expert-button"
              >
                <Phone className="h-4 w-4" /> Talk to an Expert
              </a>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-7 flex items-center gap-2 text-sm font-medium text-charcoal/70"
              data-testid="hero-trust-message"
            >
              <ShieldCheck className="h-4 w-4 text-mint" />
              Protection made simple. Support when you need it.
            </motion.p>
          </motion.div>

          <motion.div style={{ y: imgY }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="overflow-hidden rounded-[2.5rem] border border-navy/10 bg-navy shadow-lift" data-testid="hero-visual">
                <img
                  src="/images/hero.webp"
                  alt="Happy Indian family embracing outdoors on a sunny day — insurance protection for everything you care about"
                  width="1280"
                  height="960"
                  decoding="async"
                  fetchPriority="high"
                  className="aspect-[4/3.4] w-full object-cover"
                />
              </div>
              <div className="animate-float-soft absolute -left-4 top-8 hidden items-center gap-2 rounded-2xl border border-navy/10 bg-white/90 px-4 py-3 shadow-soft backdrop-blur-md sm:flex" data-testid="hero-badge-policy">
                <FileCheck2 className="h-5 w-5 text-skycyan" />
                <span className="text-xs font-bold text-navy">Policy, in your pocket</span>
              </div>
              <div className="animate-float-soft-delayed absolute -right-3 bottom-10 hidden items-center gap-2 rounded-2xl border border-navy/10 bg-white/90 px-4 py-3 shadow-soft backdrop-blur-md sm:flex" data-testid="hero-badge-shield">
                <ShieldCheck className="h-5 w-5 text-mint" />
                <span className="text-xs font-bold text-navy">Protected, always</span>
              </div>
            </motion.div>
          </motion.div>
            </>
          )}
        </div>
      </section>

      <Marquee
        items={["Chhoti Pocket", "Badi Suraksha", "Motor Insurance", "Health Insurance", "Life & Term", "Travel Insurance", "Business Insurance"]}
      />

      {/* QUICK FINDER */}
      <section className="bg-ice py-24" data-testid="quick-finder-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-skycyan-600">Start Here</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl" data-testid="quick-finder-heading">
              What Do You Want to Protect?
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {finder.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.08}>
                <Link
                  to={f.to}
                  className="group flex h-full flex-col rounded-3xl border border-navy/10 bg-white p-7 shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift"
                  data-testid={`finder-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-skycyan/15 to-mint/15 text-navy transition-colors duration-300 group-hover:from-skycyan group-hover:to-mint group-hover:text-white">
                    <f.icon className="h-7 w-7" strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold text-navy">{f.title}</h3>
                  <p className="mt-1 text-sm text-charcoal/70">{f.desc}</p>
                  <span className="mt-auto flex items-center gap-1.5 pt-5 text-sm font-bold text-mint-600">
                    Get Quote <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-white py-24" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-skycyan-600">What We Help With</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl" data-testid="services-heading">
              Insurance Solutions for Every Need
            </h2>
            <p className="mt-4 text-base leading-relaxed text-charcoal/80 md:text-lg">
              From your first vehicle to your growing business, PocketBima helps you find
              protection for what matters most.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.07} className={i === 0 ? "lg:row-span-1" : ""}>
                <div className="group flex h-full flex-col rounded-3xl border border-navy/10 bg-ice p-8 transition-all duration-300 hover:-translate-y-1.5 hover:bg-white hover:shadow-lift" data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <h3 className="font-display text-2xl font-bold text-navy">{s.title}</h3>
                  <ul className="mt-5 space-y-2.5">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-charcoal/80">
                        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-mint" /> {item}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={s.to}
                    className="mt-auto flex items-center gap-1.5 pt-7 text-sm font-bold text-navy transition-colors duration-200 group-hover:text-mint-600"
                    data-testid={`service-cta-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {s.cta} <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY POCKETBIMA */}
      <section id="why" className="bg-ice py-24" data-testid="why-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-skycyan-600">The PocketBima Way</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl" data-testid="why-heading">
              Why Choose Pocket Bima Insurance Service?
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {why.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.07}>
                <div className="group h-full rounded-3xl border border-navy/10 bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift" data-testid={`why-card-${w.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-skycyan/15 to-mint/15">
                    <w.icon className="h-6 w-6 text-skycyan-600" strokeWidth={1.9} />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold text-navy">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-charcoal/75">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-24" data-testid="how-it-works-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-skycyan-600">The Process</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl" data-testid="how-heading">
              Getting Protected Is Simple
            </h2>
          </Reveal>
          <div className="relative mt-14 grid gap-10 md:grid-cols-4 md:gap-6">
            <div className="absolute left-0 right-0 top-7 hidden h-px bg-gradient-to-r from-skycyan/40 via-mint/40 to-skycyan/40 md:block" />
            <div className="absolute bottom-0 left-7 top-0 w-px bg-gradient-to-b from-skycyan/40 via-mint/40 to-skycyan/40 md:hidden" />
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="relative pl-20 md:pl-0" data-testid={`step-${s.n}`}>
                  <span className="absolute left-0 top-0 flex h-14 w-14 items-center justify-center rounded-2xl bg-navy font-display text-lg font-extrabold text-mint shadow-soft md:relative">
                    {s.n}
                  </span>
                  <h3 className="font-display text-lg font-bold text-navy md:mt-6">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-charcoal/75">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-ice py-24" data-testid="faq-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
          <div className="grid items-start gap-12 lg:grid-cols-[0.8fr_1.2fr]">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-skycyan-600">Good To Know</p>
              <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl" data-testid="faq-heading">
                Frequently Asked Questions
              </h2>
              <p className="mt-4 max-w-md text-base leading-relaxed text-charcoal/80">
                Straight answers to the questions we hear most often. Still curious about
                something? We're one call away.
              </p>
              <a
                href={PHONE_TEL}
                className="mt-7 inline-flex items-center gap-2 rounded-full border-2 border-navy/15 px-7 py-3.5 text-sm font-bold text-navy transition-colors duration-200 hover:border-navy hover:bg-navy hover:text-white"
                data-testid="faq-call-button"
              >
                <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
              </a>
            </Reveal>
            <Reveal delay={0.12}>
              <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
                {faqs.map((f, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="rounded-2xl border border-navy/10 bg-white px-6 shadow-soft transition-shadow duration-200 data-[state=open]:shadow-lift"
                    data-testid={`faq-item-${i}`}
                  >
                    <AccordionTrigger className="py-5 font-display text-base font-bold text-navy hover:no-underline [&>svg]:text-mint-600">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-sm leading-relaxed text-charcoal/80">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      {/* RENEWAL REMINDER BANNER */}
      <section className="bg-ice pb-24" data-testid="reminder-banner-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-r from-navy to-navy-600 p-8 shadow-lift sm:p-10">
              <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-mint/20 blur-3xl" />
              <div className="relative flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
                <div className="flex items-start gap-5">
                  <span className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-mint sm:flex">
                    <BellRing className="h-7 w-7 text-white" />
                  </span>
                  <div>
                    <h2 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl" data-testid="reminder-banner-heading">
                      Policy expiring soon?
                    </h2>
                    <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base">
                      Leave your policy expiry date and we'll call you back right when your
                      renewal is due — so your protection never lapses.
                    </p>
                  </div>
                </div>
                <Link
                  to="/motor-insurance#reminder"
                  className="group flex shrink-0 items-center gap-2 rounded-full bg-mint px-7 py-3.5 text-sm font-bold text-white shadow-mint transition-all duration-200 hover:-translate-y-0.5 hover:bg-mint-600"
                  data-testid="reminder-banner-cta"
                >
                  Set a Free Reminder
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BRAND STORY — manifesto chapters */}
      <section className="relative overflow-hidden bg-navy py-24 lg:py-32" data-testid="brand-story-section">
        <div className="pointer-events-none absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-mint/10 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-skycyan/10 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 md:px-12 lg:grid-cols-2">
          <div>
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-mint">Our Story</p>
              <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="story-heading">
                Our Story: Chhoti Pocket, Badi Suraksha
              </h2>
            </Reveal>
            <div className="mt-12 space-y-12">
              {chapters.map((c, i) => (
                <Reveal key={c.n} delay={i * 0.1}>
                  <div className="flex gap-6" data-testid={`story-chapter-${c.n}`}>
                    <span className="font-display text-4xl font-extrabold text-mint/40">{c.n}</span>
                    <div>
                      <h3 className="font-display text-xl font-bold text-white md:text-2xl">{c.title}</h3>
                      <p className="mt-3 max-w-md text-sm leading-relaxed text-white/65 md:text-base">{c.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-[2.5rem] border border-white/10 shadow-lift" data-testid="story-visual">
              <img
                src="/images/story.webp"
                alt="Cheerful Indian family sitting together on the grass in a park — everything you care about, protected within reach"
                width="1280"
                height="841"
                loading="lazy"
                decoding="async"
                className="aspect-[4/4.2] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FOUNDER STRIP */}
      <section className="bg-white py-16" data-testid="founder-strip-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
          <Reveal>
            <Link
              to="/about"
              className="group flex flex-col items-start gap-6 rounded-[2rem] border border-navy/10 bg-ice p-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift sm:flex-row sm:items-center sm:gap-8 sm:p-10"
              data-testid="founder-strip-card"
            >
              <img
                src="/images/founder.webp"
                alt="Gaurav Singh, Founder of Pocket Bima Insurance Service"
                width="640"
                height="640"
                loading="lazy"
                decoding="async"
                className="h-20 w-20 shrink-0 rounded-full border-2 border-mint object-cover shadow-soft sm:h-24 sm:w-24"
                data-testid="founder-strip-photo"
              />
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-skycyan-600">Meet Our Founder</p>
                <p className="mt-2 font-display text-xl font-bold leading-snug text-navy sm:text-2xl">
                  "I started PocketBima so insurance would never feel out of reach."
                </p>
                <p className="mt-2 text-sm font-medium text-charcoal/60">
                  Gaurav Singh — Founder, PocketBima · 7+ years in insurance
                </p>
              </div>
              <span
                className="flex shrink-0 items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-bold text-white transition-colors duration-200 group-hover:bg-mint"
                data-testid="founder-strip-cta"
              >
                Read his message <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section id="quote" className="bg-ice py-24" data-testid="quote-section">
        <div className="mx-auto grid max-w-7xl items-start gap-14 px-5 sm:px-8 md:px-12 lg:grid-cols-[1fr_1.2fr]">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-skycyan-600">Get Started</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl" data-testid="quote-heading">
              Let's Find the Right Protection for You
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-charcoal/80">
              Tell us what you want to protect and our team will get in touch with guidance on
              suitable options.
            </p>
            <div className="mt-8 space-y-4">
              <a href={PHONE_TEL} className="flex items-center gap-3 text-navy transition-colors hover:text-mint-600" data-testid="quote-call-link">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy"><Phone className="h-5 w-5 text-mint" /></span>
                <span className="font-bold">{PHONE_DISPLAY}</span>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-navy transition-colors hover:text-mint-600" data-testid="quote-whatsapp-link">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-mint"><MessagesSquare className="h-5 w-5 text-white" /></span>
                <span className="font-bold">Chat with PocketBima</span>
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-[2rem] border border-navy/10 bg-white p-7 shadow-soft sm:p-10">
              <QuoteForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTACT + MAP */}
      <section className="bg-white py-24" data-testid="contact-strip-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl" data-testid="contact-strip-heading">
              Let's Talk About Your Insurance Needs
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {[
              { label: "Office", value: ADDRESS, href: null, testid: "contact-office" },
              { label: "Phone", value: PHONE_DISPLAY, href: PHONE_TEL, cta: `Call ${PHONE}`, testid: "contact-phone" },
              { label: "Email", value: EMAIL, href: EMAIL_MAILTO, cta: "Email Us", testid: "contact-email" },
            ].map((c, i) => (
              <Reveal key={c.label} delay={i * 0.08}>
                <div className="flex h-full flex-col rounded-3xl border border-navy/10 bg-ice p-8" data-testid={c.testid}>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-skycyan-600">{c.label}</p>
                  <p className="mt-3 text-base font-semibold leading-relaxed text-navy">{c.value}</p>
                  {c.href && (
                    <a href={c.href} className="mt-auto inline-flex w-fit items-center gap-1.5 pt-6 text-sm font-bold text-mint-600" data-testid={`${c.testid}-cta`}>
                      {c.cta} <ArrowRight className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 overflow-hidden rounded-[2rem] border border-navy/10 shadow-soft" data-testid="contact-map">
              <iframe
                title="PocketBima office location — Greater Noida West"
                src={MAPS_EMBED}
                className="h-[380px] w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
